import React, { useState, useEffect, useCallback } from 'react';
import { Agent } from '../types';
import { listAgents, listAllStdRequests, StdRequestLog } from '../api/client';

// Cross-agent view of the /api/std request log: the raw system prompt, messages
// and response of every generate/stream call — including stateless ones
// (summaries/translations/compliance) that leave no Memory thread.
export function RequestsPage({ embedded = false }: { embedded?: boolean } = {}) {
  const [rows, setRows] = useState<StdRequestLog[]>([]);
  const [agents, setAgents] = useState<Record<string, Agent>>({});
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const agentsData = await listAgents();
      const agentMap: Record<string, Agent> = {};
      agentsData.forEach((a) => { agentMap[a.id] = a; });
      setAgents(agentMap);
      setRows(await listAllStdRequests(agentsData.map((a) => a.id)));
    } catch {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        {embedded ? <span /> : <h2 style={styles.heading}>Requests</h2>}
        <button style={styles.refreshBtn} onClick={load}>Refresh</button>
      </div>

      {loading ? (
        <div style={styles.muted}>Loading...</div>
      ) : rows.length === 0 ? (
        <div style={styles.muted}>No /api/std requests logged yet.</div>
      ) : (
        <div style={styles.list}>
          {rows.map((r) => {
            const agentName = agents[r.agentId || '']?.name || (r.agentId || '').slice(0, 8);
            const isOpen = selected === r.id;
            const ok = !r.status || (r.status >= 200 && r.status < 300);
            return (
              <div key={r.id} style={styles.card}>
                <div style={styles.cardHeader} onClick={() => setSelected(isOpen ? null : r.id)}>
                  <div style={styles.cardLeft}>
                    <span style={styles.agentName}>{agentName}</span>
                    <code style={styles.agentId}>{r.agentId}</code>
                    <span style={{
                      ...styles.badge,
                      background: r.stream ? '#ede9fe' : '#eff6ff',
                      color: r.stream ? '#6d28d9' : '#2563eb',
                    }}>
                      {r.stream ? 'stream' : 'generate'}
                    </span>
                    {r.threadId ? (
                      <span style={{ ...styles.badge, background: '#f0fdf4', color: '#16a34a' }}>memory</span>
                    ) : (
                      <span style={{ ...styles.badge, background: '#fef3c7', color: '#92400e' }}>stateless</span>
                    )}
                    {r.status && (
                      <span style={{
                        ...styles.badge,
                        background: ok ? '#f3f4f6' : '#fee2e2',
                        color: ok ? '#6b7280' : '#dc2626',
                      }}>
                        {r.status}
                      </span>
                    )}
                  </div>
                  <div style={styles.cardRight}>
                    <span style={styles.date}>{r.createdAt ? new Date(r.createdAt).toLocaleString() : ''}</span>
                    <span style={styles.chevron}>{isOpen ? '▲' : '▼'}</span>
                  </div>
                </div>
                {isOpen && (
                  <div style={styles.body}>
                    {r.threadId && (
                      <div style={styles.metaLine}>
                        <strong>thread:</strong> <code>{r.threadId}</code>
                        {r.resourceId && <> &nbsp; <strong>resource:</strong> <code>{r.resourceId}</code></>}
                      </div>
                    )}
                    {r.system && (
                      <div style={styles.block}>
                        <div style={styles.blockLabel}>SYSTEM / INSTRUCTIONS</div>
                        <pre style={styles.pre}>{r.system}</pre>
                      </div>
                    )}
                    {r.messages && r.messages.length > 0 && (
                      <div style={styles.block}>
                        <div style={styles.blockLabel}>MESSAGES SENT</div>
                        {r.messages.map((m, i) => (
                          <div key={i} style={styles.msg}>
                            <span style={{ ...styles.msgRole, color: m.role === 'assistant' ? '#16a34a' : '#2563eb' }}>{m.role}:</span> {m.content}
                          </div>
                        ))}
                      </div>
                    )}
                    {r.response && (
                      <div style={styles.block}>
                        <div style={styles.blockLabel}>RESPONSE</div>
                        <pre style={styles.pre}>{r.response}</pre>
                      </div>
                    )}
                    {r.usage != null && (
                      <div style={styles.metaLine}><strong>usage:</strong> <code>{JSON.stringify(r.usage)}</code></div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { padding: '20px', flex: 1, overflowY: 'auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  heading: { margin: 0, fontSize: 18, color: '#1a1a1a' },
  refreshBtn: { padding: '4px 12px', borderRadius: 6, border: '1px solid #ddd', background: '#fff', fontSize: 12, cursor: 'pointer', color: '#555' },
  muted: { color: '#888', fontSize: 14 },
  list: { display: 'flex', flexDirection: 'column', gap: 8 },
  card: { border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', cursor: 'pointer', background: '#fafafa' },
  cardLeft: { display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flexWrap: 'wrap' },
  cardRight: { display: 'flex', alignItems: 'center', gap: 8 },
  agentName: { fontSize: 13, fontWeight: 600, color: '#1a1a1a' },
  agentId: { fontSize: 11, color: '#aaa' },
  badge: { fontSize: 10, padding: '1px 6px', borderRadius: 4, fontWeight: 600 },
  date: { fontSize: 11, color: '#aaa' },
  chevron: { fontSize: 10, color: '#aaa' },
  body: { padding: '12px 16px', background: '#fff', borderTop: '1px solid #e5e7eb' },
  metaLine: { fontSize: 12, color: '#555', marginBottom: 8 },
  block: { marginBottom: 10 },
  blockLabel: { fontSize: 10, fontWeight: 700, letterSpacing: 0.5, color: '#9ca3af', marginBottom: 4 },
  pre: { margin: 0, padding: '8px 10px', background: '#f9fafb', borderRadius: 6, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: 12, lineHeight: 1.5, color: '#374151', maxHeight: 320, overflowY: 'auto' },
  msg: { padding: '4px 0', borderBottom: '1px solid #f3f4f6', fontSize: 13, color: '#1a1a1a', lineHeight: 1.4 },
  msgRole: { fontSize: 11, fontWeight: 700, textTransform: 'uppercase' as const, marginRight: 4 },
};
